/* Copyright 2026 WinSolutions
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
 */
import { useEffect, useState } from "react";
import { Button } from "@vector-im/compound-web";
import styles from "./MeetingModeration.module.css";
import { RoomEvent, type Room, type RemoteParticipant } from "livekit-client";
import type { Room as MatrixRoom } from "matrix-js-sdk";
import { parseParticipantIdentity } from "../matrix/participantIdentity";

const topic = "klava.meeting.moderation.v1";
function mayModerate(room: MatrixRoom, identity: string): boolean {
  const id = parseParticipantIdentity(identity)?.userId;
  if (!id) return false;
  const creator = room.currentState
    .getStateEvents("m.room.create", "")
    ?.getSender();
  const levels = room.currentState
    .getStateEvents("m.room.power_levels", "")
    ?.getContent();
  return (
    id === creator || (levels?.users?.[id] ?? levels?.users_default ?? 0) >= 50
  );
}

/** Private requests use LiveKit destination identities, never chat messages. */
export function MeetingModeration({
  room,
  matrixRoom,
  mute,
  showControls = true,
}: {
  room: Room;
  matrixRoom: MatrixRoom;
  mute: () => void;
  showControls?: boolean;
}) {
  const [notice, setNotice] = useState("");
  const [people, setPeople] = useState<RemoteParticipant[]>([]);
  const [target, setTarget] = useState("");
  const [error, setError] = useState("");
  const allowed = mayModerate(matrixRoom, room.localParticipant.identity);
  useEffect(() => {
    const refresh = () =>
      setPeople(Array.from(room.remoteParticipants.values()));
    refresh();
    room.on(RoomEvent.ParticipantConnected, refresh);
    room.on(RoomEvent.ParticipantDisconnected, refresh);
    return () => {
      room.off(RoomEvent.ParticipantConnected, refresh);
      room.off(RoomEvent.ParticipantDisconnected, refresh);
    };
  }, [room]);
  useEffect(() => {
    const receive = (
      bytes: Uint8Array,
      sender?: RemoteParticipant,
      _kind?: unknown,
      receivedTopic?: string,
    ) => {
      if (
        receivedTopic !== topic ||
        !sender ||
        bytes.length > 1024 ||
        !mayModerate(matrixRoom, sender.identity)
      )
        return;
      try {
        const message = JSON.parse(new TextDecoder().decode(bytes));
        if (
          !Number.isFinite(message.at) ||
          Math.abs(Date.now() - message.at) > 15000
        )
          return;
        if (message.action === "quiet") {
          mute();
          setNotice(
            "Организатор включил «Тишина в зале». Ваш микрофон выключен.",
          );
        } else if (
          message.action === "request-off" &&
          message.target === room.localParticipant.identity
        )
          setNotice("Организатор просит вас выключить микрофон.");
      } catch {
        /* Ignore malformed remote data. */
      }
    };
    room.on(RoomEvent.DataReceived, receive);
    return () => {
      room.off(RoomEvent.DataReceived, receive);
    };
  }, [room, matrixRoom, mute]);
  async function send(action: "quiet" | "request-off") {
    if (!mayModerate(matrixRoom, room.localParticipant.identity)) return;
    if (action === "request-off" && !room.remoteParticipants.has(target))
      return;
    setError("");
    try {
      await room.localParticipant.publishData(
        new TextEncoder().encode(
          JSON.stringify({ action, at: Date.now(), target }),
        ),
        {
          reliable: true,
          topic,
          ...(action === "request-off"
            ? { destinationIdentities: [target] }
            : {}),
        },
      );
    } catch {
      setError("Не удалось отправить команду. Попробуйте ещё раз.");
    }
  }
  return (
    <>
      {allowed && showControls && (
        <div aria-label="Управление микрофонами" className={styles.controls}>
          <Button kind="secondary" size="sm" onClick={() => void send("quiet")}>
            Тишина в зале
          </Button>
          <select
            className={styles.participant}
            aria-label="Участник"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          >
            <option value="">Выберите участника</option>
            {people.map((p) => (
              <option key={p.identity} value={p.identity}>
                {p.name ||
                  parseParticipantIdentity(p.identity)?.userId ||
                  p.identity}
              </option>
            ))}
          </select>
          <Button
            kind="secondary"
            size="sm"
            disabled={!target}
            onClick={() => void send("request-off")}
          >
            Попросить выключить микрофон
          </Button>
          {error && (
            <span className={styles.error} role="alert">
              {error}
            </span>
          )}
        </div>
      )}
      {notice && (
        <div
          role="alertdialog"
          aria-label="Сообщение организатора"
          className={styles.notice}
        >
          <p>{notice}</p>
          {notice.includes("просит") && (
            <Button
              kind="secondary"
              size="sm"
              onClick={() => {
                mute();
                setNotice("");
              }}
            >
              Выключить микрофон
            </Button>
          )}
          <Button kind="secondary" size="sm" onClick={() => setNotice("")}>
            Закрыть
          </Button>
        </div>
      )}
    </>
  );
}
